/**
 * The conversation list beside the desktop chat, under the two-column rule:
 * below `WIDE_DESKTOP_MIN_WIDTH`, the screen shows the chat and one other
 * column at most. A panel opening on the right (a document, a table of people,
 * a company) folds the list away, and gives it back when it closes; asked for
 * while the panel is open, the list comes over the chat instead of beside it.
 */
export type ConversationListState = {
  readonly isOpen: boolean;
  /**
   * Folded by a panel rather than by the user: reopened when the panel
   * closes, so the list the user had out comes back by itself.
   */
  readonly isFoldedByPanel: boolean;
};

export type ConversationListEvent =
  /** The user's toggle in the chat header. */
  | { readonly type: "toggle" }
  /** Put away for good — a click beside the list laid over the chat. */
  | { readonly type: "close" }
  /** A panel opened on a screen with room for two columns only. */
  | { readonly type: "panelOpened" }
  /** That panel closed, or the screen grew wide enough for three. */
  | { readonly type: "panelClosed" };

/** Where the list sits: beside the chat, over it, or folded away. */
export type ConversationListLayout = "inline" | "overlay" | "folded";

export const INITIAL_CONVERSATION_LIST_STATE: ConversationListState = {
  isOpen: true,
  isFoldedByPanel: false,
};

/** The list after `event`. */
export function nextConversationListState(
  state: ConversationListState,
  event: ConversationListEvent,
): ConversationListState {
  switch (event.type) {
    case "toggle":
      return { isOpen: !state.isOpen, isFoldedByPanel: false };
    case "close":
      return { isOpen: false, isFoldedByPanel: false };
    case "panelOpened":
      return state.isOpen ? { isOpen: false, isFoldedByPanel: true } : state;
    case "panelClosed":
      return state.isFoldedByPanel
        ? { isOpen: true, isFoldedByPanel: false }
        : state;
  }
}

/**
 * Where an open list is drawn: over the chat while a panel holds the second
 * column of a screen that has room for two, beside it otherwise.
 */
export function conversationListLayout({
  isOpen,
  isCrowded,
}: {
  readonly isOpen: boolean;
  /** A panel is open on a screen narrower than three columns need. */
  readonly isCrowded: boolean;
}): ConversationListLayout {
  if (!isOpen) return "folded";
  return isCrowded ? "overlay" : "inline";
}
